import { nonEmptyString } from './format';
import type { ProofGraph, Claim, Element, Fact, Preferences, Template, ValenceType } from '../types';

export const INVARIANTS = {
  type: 'invariant',
  _tier: 'locked',
  items: [
    {
      id: 'weakest-link',
      name: 'Weakest-link claim floor',
      desc: 'Claim floor = min(element tier). Never an average.',
    },
    {
      id: 'conjunctive',
      name: 'Conjunctive elements',
      desc: 'Every element must be met. A miss is a hole, not a deduction.',
    },
    {
      id: 'two-sided',
      name: 'Two-sided independent scoring',
      desc: 'Your tier and opponent pressure are scored separately, never collapsed.',
    },
    {
      id: 'four-layer',
      name: 'Four-layer proof graph',
      desc: 'Party → claim → element → fact → evidence. Shape fixed; contents are data.',
    },
  ],
};

// Calculate element tier (0..4) and threat (level & pct) based on the facts
// linked to it. Follows the spec: strongest supporting fact sets the floor;
// facts don't sum linearly.
//
// `links` is the element's fact_links resolved against the shared fact pool
// — each entry pairs a pooled Fact with the valence *this element* assigns
// it (the same fact can support one element and rebut another).
export function computeElementScores(
  links: { fact: Fact; valence: ValenceType; strength?: number | undefined }[],
  isClientSide: boolean
): { tier: number; threat: { level: 'none' | 'low' | 'contested' | 'favored'; party_label: string; pct: number } } {
  const facts = links.map(l => ({ status: l.fact.status, valence: l.valence, strength: l.strength }));

  if (facts.length === 0) {
    return {
      tier: 0,
      threat: { level: 'none', party_label: isClientSide ? 'Defendant' : 'Plaintiff (Us)', pct: 0 },
    };
  }

  let maxSupport = 0; // 0..4
  let maxContrary = 0; // 0..4

  // Map valence types to values (positive supports, negative is contrary)
  // For client side, positive supports client. For adverse side, we flip or keep distinct.
  // In our model, we store valence *from client's perspective*.
  // So positive valence helps client, negative helps opponent.
  for (const fact of facts) {
    let supportVal = 0;
    let contraryVal = 0;

    // Skip alleged-only facts for computing high strength (must have evidence for full weight)
    const multiplier = fact.status === 'alleged' ? 0.3 : fact.status === 'established' ? 1.2 : 1.0;

    const impactStrength = fact.strength ?? defaultFactLinkStrength(fact.valence);
    const impactTier = impactStrength <= 2 ? 1 : impactStrength <= 5 ? 2 : impactStrength <= 8 ? 3 : 4;

    switch (fact.valence) {
      case 'StronglySupports':
      case 'Supports':
      case 'LeansToward':
		supportVal = impactTier;
        break;
      case 'StronglyRebuts':
      case 'Rebuts':
      case 'Undercuts':
		contraryVal = impactTier;
        break;
      case 'Disputed':
		supportVal = impactTier;
		contraryVal = impactTier;
        break;
    }

    const effectiveSupport = Math.min(4, Math.round(Number(supportVal) * multiplier));
    const effectiveContrary = Math.min(4, Math.round(Number(contraryVal) * multiplier));

    if (effectiveSupport > maxSupport) {
      maxSupport = effectiveSupport;
    }
    if (effectiveContrary > maxContrary) {
      maxContrary = effectiveContrary;
    }
  }

  // Corroboration bonus: if we have multiple supporting facts, bump the tier slightly
  let supportingCount = 0;
  for (const fact of facts) {
    if (['StronglySupports', 'Supports', 'LeansToward'].includes(fact.valence)) {
      supportingCount++;
    }
  }
  
  let computedTier = maxSupport;
  if (supportingCount > 1 && computedTier > 0 && computedTier < 4) {
    computedTier += 1; // minor corroboration boost
  }

  // Determine threat level (coarse 4-level scale)
  let threatLevel: 'none' | 'low' | 'contested' | 'favored';
  let threatPct: number;

  if (maxContrary === 0) {
    threatLevel = 'none';
    threatPct = 0;
  } else if (maxContrary <= 2) {
    threatLevel = 'low';
    threatPct = 30;
  } else if (maxContrary === 3) {
    threatLevel = 'contested';
    threatPct = 65;
  } else {
    threatLevel = 'favored';
    threatPct = 90;
  }

  // If this is an adverse claim, we invert the meaning:
  // Opponent carries the burden, so we want high contrary score (which lowers their tier)
  if (!isClientSide) {
    // Invert roles for calculation
    computedTier = maxContrary;
    
    if (maxSupport === 0) {
      threatLevel = 'none';
      threatPct = 0;
    } else if (maxSupport <= 2) {
      threatLevel = 'low';
      threatPct = 30;
    } else if (maxSupport === 3) {
      threatLevel = 'contested';
      threatPct = 65;
    } else {
      threatLevel = 'favored';
      threatPct = 90;
    }
  }

  return {
    tier: computedTier,
    threat: {
      level: threatLevel,
      party_label: isClientSide ? 'Defendant' : 'Plaintiff (Us)',
      pct: threatPct,
    },
  };
}

// Preserve the old valence-only scoring for legacy links while making new
// links' explicit 1-10 impact strength authoritative.
export function defaultFactLinkStrength(valence: ValenceType): number {
  switch (valence) {
    case 'StronglySupports':
    case 'StronglyRebuts': return 10;
    case 'Supports':
    case 'Rebuts': return 7;
    case 'LeansToward':
    case 'Undercuts': return 4;
    case 'Disputed': return 2;
  }
}

// Controller-served model resolver
export class ConfigController {
  private templates: Template[];
  private prefs: Preferences;

  constructor(templates: Template[], prefs: Preferences) {
    this.templates = (templates ?? []);
    this.prefs = prefs;
  }

  // Merges the template configs, preference tweaks, and invariants for a given matter graph
  resolve(matterGraph: ProofGraph): { template: Template; stages: { widgets: import("../types").WidgetPlacement[]; id: string; name: string; desc: string; cadence_days: number; terminal: boolean; }[]; floatingStates: import("../types").FloatingState[]; claims: Claim[]; prefs: Preferences; invariants: { type: string; _tier: string; items: { id: string; name: string; desc: string; }[]; }; } {
    const templateID = (nonEmptyString(matterGraph?.template_id) ?? 'civil-ne');
    const templatesList = (this.templates ?? []);
    const defaultTemplate: Template = {
      id: 'civil-ne',
      name: 'Civil litigation — New England',
      rev: 1,
      is_default: true,
      stages: [
        { id: 'presuit', name: 'Pre-suit', desc: 'Demand, investigation, tolling watch', cadence_days: 14, terminal: false },
        { id: 'pleadings', name: 'Pleadings', desc: 'Complaint, answer, cross-claims, impleader', cadence_days: 21, terminal: false },
        { id: 'discovery', name: 'Discovery', desc: 'Written discovery, depositions, meet-and-confer', cadence_days: 30, terminal: false },
        { id: 'pretrial', name: 'Pretrial', desc: 'Pretrial conference, jury instructions, exhibits', cadence_days: 14, terminal: false },
        { id: 'trial', name: 'Trial', desc: 'Opening, evidence, jury deliberations, verdict', cadence_days: 7, terminal: false },
        { id: 'judgment', name: 'Judgment / collection', desc: 'Judgment entry, execution, appeals', cadence_days: 45, terminal: true }
      ],
      floating_states: [],
      claims: [],
      stage_actions: {},
      trigger_sets: {},
      audiences: { available: [], default: '' }
    };
    const template = (templatesList.find((t) => t.id === templateID) ?? templatesList[0] ?? defaultTemplate);
    const stages = ((template?.stages ?? [])).map((stage) => {
      // 1. Matter-level detail override (saved in proofGraph/matterGraph)
      let widgets = matterGraph?.stage_widgets?.[stage.id];

      // 2. Preferences default mapping override
      if (!widgets) {
        const layoutType = (nonEmptyString(this.prefs?.stage_workspace_defaults?.[stage.id]) ?? 'balanced');
        if (layoutType === 'documentarian') {
          widgets = [
            { id: `w_doc_${stage.id}`, type: 'document', w: 3, h: 2 },
            { id: `w_tsk_${stage.id}`, type: 'task', w: 1, h: 2 },
            { id: `w_nte_${stage.id}`, type: 'note', w: 2, h: 2 },
          ];
        } else if (layoutType === 'trial-lawyer') {
          widgets = [
            { id: `w_cal_${stage.id}`, type: 'calendar', w: 3, h: 2 },
            { id: `w_tsk_${stage.id}`, type: 'task', w: 1, h: 2 },
            { id: `w_nte_${stage.id}`, type: 'note', w: 2, h: 2 },
          ];
        } else if (layoutType === 'motions-practitioner') {
          widgets = [
            { id: `w_act_${stage.id}`, type: 'action', w: 3, h: 1 },
            { id: `w_cal_${stage.id}`, type: 'calendar', w: 1, h: 2 },
            { id: `w_nte_${stage.id}`, type: 'note', w: 2, h: 2 },
          ];
        }
      }

      // 3. Fallback to active template default
      if (!widgets) {
        widgets = stage.widgets ?? [
          { id: `w_act_${stage.id}`, type: 'action', w: 3, h: 1 },
          { id: `w_tsk_${stage.id}`, type: 'task', w: 1, h: 2 },
          { id: `w_nte_${stage.id}`, type: 'note', w: 2, h: 2 },
        ];
      }

      return {
        ...stage,
        widgets,
      };
    });

    // The shared, flat fact pool — elements point at facts by id via
    // fact_links, so scoring needs to resolve those links against it.
    const factsPool: Record<string, Fact> = {};
    ((matterGraph.facts ?? [])).forEach((f: Fact) => { factsPool[f.id] = f; });

    // Hydrate claims with computed scores and narratives
    const claims: Claim[] = ((matterGraph.claims ?? [])).map((claim) => {
      const isClientSide = claim.side === 'client';

      // Recompute element scores
      const elements: Element[] = ((claim.elements ?? [])).map((el) => {
        const links = (((el.fact_links ?? [])) as { fact_id: string; valence: ValenceType; strength?: number }[])
          .flatMap((link) => {
            const fact = factsPool[link.fact_id];
            return fact ? [{ fact, valence: link.valence, strength: link.strength }] : [];
          });
        const scores = computeElementScores(links, isClientSide);
        return {
          ...el,
          tier: scores.tier,
          threat: {
            ...el.threat,
            level: scores.threat.level,
            party_label: scores.threat.party_label,
            pct: scores.threat.pct,
          },
          is_gap: scores.tier === 0,
        };
      });

      // Derive floor tier = min(element.tier) per system invariant
      let floorTier = 4;
      if (elements.length === 0) {
        floorTier = 0;
      } else {
        for (const el of elements) {
          if (el.tier < floorTier) {
            floorTier = el.tier;
          }
        }
      }

      return {
        ...claim,
        elements,
        floor_tier: floorTier,
      };
    });

    return {
      template,
      stages,
      floatingStates: (template?.floating_states ?? []),
      claims,
      prefs: this.prefs,
      invariants: INVARIANTS,
    };
  }
}
